import { useState, useEffect } from 'react';

export default function TutorsSection() {
  const [tutors, setTutors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTutor, setNewTutor] = useState({ name: '', email: '', boards: '', level: 'GCSE' });
  const [added, setAdded] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  const fetchTutors = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/users?role=tutor`, {
        headers: { 
          'Authorization': `Bearer ${token}` 
        }
      });
      if (res.ok) {
        const data = await res.json();
        setTutors(data);
      }
    } catch (error) {
      console.error('Failed to fetch tutors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTutors();
  }, []);

  const toggleStatus = async (id: string) => {
    // Backend status update logic could go here
    setTutors((prev) => prev.map((t) => t._id === id ? { ...t, status: t.status === 'inactive' ? 'active' : 'inactive' } : t));
  };

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setAdded(true);
    setShowAdd(false);
    setNewTutor({ name: '', email: '', boards: '', level: 'GCSE' });
    setTimeout(() => setAdded(false), 3000);
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Loading tutors...</div>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>Tutor Management</h2>
          <p className="text-slate-500 text-sm">{tutors.length} tutors on the platform</p>
        </div>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-user-add-line"></i> Invite Tutor
        </button>
      </div>

      {added && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-lg">
          <i className="ri-check-line"></i> Tutor invitation sent successfully!
        </div>
      )}

      {showAdd && (
        <div className="bg-white rounded-xl border border-emerald-200 p-5">
          <h3 className="font-bold text-slate-800 text-sm mb-4">Invite New Tutor</h3>
          <form onSubmit={handleAdd} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Full Name</label>
              <input required value={newTutor.name} onChange={(e) => setNewTutor({ ...newTutor, name: e.target.value })} placeholder="Dr. Jane Smith" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
              <input required type="email" value={newTutor.email} onChange={(e) => setNewTutor({ ...newTutor, email: e.target.value })} placeholder="tutor@email.com" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Exam Boards</label>
              <input value={newTutor.boards} onChange={(e) => setNewTutor({ ...newTutor, boards: e.target.value })} placeholder="AQA, OCR" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Level</label>
              <select value={newTutor.level} onChange={(e) => setNewTutor({ ...newTutor, level: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white">
                <option>GCSE</option>
                <option>A-Level</option>
                <option>GCSE &amp; A-Level</option>
              </select>
            </div>
            <div className="col-span-2 flex gap-3 justify-end">
              <button type="button" onClick={() => setShowAdd(false)} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2 cursor-pointer whitespace-nowrap">Cancel</button>
              <button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Send Invitation</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {tutors.map((tutor) => (
          <div key={tutor._id} className="bg-white rounded-xl border border-slate-100 p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-slate-100 flex items-center justify-center">
                {tutor.avatar ? (
                  <img src={tutor.avatar} alt={tutor.name} className="w-full h-full object-cover object-top" />
                ) : (
                  <span className="text-sm font-bold text-slate-400 capitalize">{tutor.name.charAt(0)}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-bold text-slate-800 text-sm truncate">{tutor.name}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${(tutor.status || 'active') === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {tutor.status || 'active'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mb-2">{tutor.email}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {(tutor.boards || ['AQA', 'OCR']).map((b: string) => (
                    <span key={b} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full font-medium">{b}</span>
                  ))}
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">{tutor.level || 'GCSE & A-Level'}</span>
                </div>
                <div className="flex gap-4 text-xs text-slate-500">
                  <span><i className="ri-video-line mr-1"></i>{tutor.sessions || 0} sessions</span>
                  <span><i className="ri-user-line mr-1"></i>{tutor.students || 0} students</span>
                  <span><i className="ri-star-fill mr-1 text-amber-400"></i>{tutor.rating || '5.0'}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-50">
              <button onClick={() => toggleStatus(tutor._id)} className={`flex-1 text-xs font-semibold py-2 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${(tutor.status || 'active') === 'active' ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}>
                {(tutor.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}
              </button>
              <button className="flex-1 text-xs font-semibold py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
                View Profile
              </button>
              <button className="flex-1 text-xs font-semibold py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white transition-colors cursor-pointer whitespace-nowrap">
                Assign Session
              </button>
            </div>
          </div>
        ))}
        {tutors.length === 0 && (
          <div className="col-span-2 text-center py-12 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">
            No tutors found in the system.
          </div>
        )}
      </div>
    </div>
  );
}
